import React, { useState } from 'react';
import { MessageCircle, Send } from 'lucide-react';
import { authenticatedFetch } from '../../utils/api';
import styles from './PresentationCoach.module.css';
import ConversationDisplay from '../ConversationDisplay/ConversationDisplay';

const PresentationCoach = ({ presentationId, videoOnlyPractice }) => {
    const [question, setQuestion] = useState('');
    const [messages, setMessages] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const suggestedQuestions = videoOnlyPractice
        ? ['Summarize my practice results.']
        : [
            'Refine my transcript.',
            'Any delivery tips based on my transcript?',
            'Summarize my practice results.'
        ];

    const askCoach = async (questionText = question) => {
        if (!questionText.trim()) return;

        const userMessage = {
            role: 'user',
            content: questionText,
            timestamp: new Date().toISOString()
        };

        // Optimistically add user message
        setMessages(prev => [...prev, userMessage]);
        setQuestion('');
        setIsLoading(true);
        setError(null);

        try {
            const response = await authenticatedFetch(`/api/presentation/ask-coach`, {
                method: 'POST',
                body: {
                    presentationId: presentationId,
                    question: questionText,
                    conversationHistory: messages
                }
            });

            const botMessage = {
                role: 'bot',
                content: response.answer,
                timestamp: new Date().toISOString()
            };

            setMessages(prev => [...prev, botMessage]);
        } catch (err) {
            setError(err.message || 'Failed to get coach response');
            const errorMessage = {
                role: 'error',
                content: err.message || 'Something went wrong',
                timestamp: new Date().toISOString()
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            askCoach();
        }
    };

    return (
        <div className={styles.coachContainer}>
            <div className={styles.coachHeader}>
                <MessageCircle size={24} className={styles.coachHeaderIcon} />
                <h3>Presentation AI Coach</h3>
            </div>

            <div className={styles.coachMessagesContainer}>
                {messages.length === 0 && (
                    <div className={styles.coachWelcomeMessage}>
                        <div className={styles.welcomeContent}>
                            <h4>Welcome to Your AI Presentation Coach</h4>
                            <p>Ask any questions about your current Presentation.</p>
                            <div className={styles.suggestedQuestions}>
                                <h5>Try asking:</h5>
                                <ul>
                                    {suggestedQuestions.map((q, index) => (
                                        <li
                                            key={index}
                                            onClick={() => askCoach(q)}
                                            className={styles.clickableSuggestion}
                                        >
                                            {q}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                )}

                <ConversationDisplay
                    conversationHistory={messages}
                    isTyping={isLoading}
                    error={error}
                    speakMessage={() => { }}
                    isBotSpeaking={false}
                    isPresentationCoach={true}
                />
            </div>

            <div className={styles.coachInputContainer}>
                <textarea
                    placeholder="Ask your AI coach a question about your presentation..."
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    onKeyPress={handleKeyPress}
                    rows={3}
                    disabled={isLoading}
                    className={styles.coachInput}
                />
                <button
                    onClick={() => askCoach()}
                    disabled={isLoading || !question.trim()}
                    className={styles.coachSendButton}
                >
                    <Send size={20} />
                </button>
            </div>
        </div>
    );
};

export default PresentationCoach;